import React, { useEffect, useMemo } from "react";
import { ThemeProvider } from "./components/CommonComponents/ThemeProvider";
import { NotificationProvider } from "./context/NotificationContext";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import SettingsModal from "./components/CommonComponents/SettingsModal";
import NetworkStatusNotification from "./components/CommonComponents/NetworkStatusNotification";
import SessionExpiredModal from "./components/CommonComponents/SessionExpiredModal";
import ForcePasswordChangeModal from "./components/CommonComponents/ForcePasswordChangeModal";
import AppErrorBoundary from "./components/CommonComponents/AppErrorBoundary";
import { Provider } from "react-redux";
import { store } from "./store/store";
import useModalBackNavigationGuard from "./hooks/useModalBackNavigationGuard";

import POSLogin from "./pages/POSLogin";
import Login from "./pages/Login";
import SuperAdminLoginGate from "./pages/SuperAdminLoginGate";

import { adminRoutes } from "./routes/adminRoutes";
import { posRoutes } from "./routes/posRoutes";
import { superadminRoutes } from "./routes/superadminRoutes";


function App() {
  useEffect(() => {
    // Explicitly trigger rehydration on mount
    store.dispatch({ type: "@@INIT" });
  }, []);

  function ModalBackNavigationGuard() {
    useModalBackNavigationGuard();
    return null;
  }

  function ModalOverlay() {
    const location = useLocation();
    const navigate = useNavigate();
    // Only show modal for /settings route (can expand for more modals)
    // Show modal for /admin/settings (admin), /superadmin/settings (superadmin), or /settings (fallback)
    const isSettingsOpen = ["/admin/settings", "/superadmin/settings", "/settings"].includes(location.pathname);
    const initialSection = new URLSearchParams(location.search).get("section");

    // Example sections for SettingsModal (customize as needed)
    const sections = useMemo(() => [
      { title: "Profile", content: "Manage your profile information." },
      { title: "Notifications", content: "Set your notification preferences." },
      { title: "Security", content: "Update your password and security settings." },
    ], []);

    if (!isSettingsOpen) return null;
    return (
      <SettingsModal
        isOpen={true}
        onClose={() => navigate(-1)}
        initialSection={initialSection}
        sections={sections}
      />
    );
  }

  return (
    <Provider store={store}>
      <Router>
        <Routes>
          {/* Public login routes (no ThemeProvider, no NotificationProvider) */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<POSLogin />} />
          <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/login" element={<Login role="admin" />} />
          <Route path="/superadmin/login" element={<SuperAdminLoginGate />} />

          {/* Protected/dashboard routes wrapped in ThemeProvider AND NotificationProvider */}
          <Route
            path="/*"
            element={
              <AppErrorBoundary>
                <NotificationProvider>
                  <ThemeProvider>
                    <>
                      <ModalBackNavigationGuard />
                      <Routes>
                        {adminRoutes.map(r => <Route key={r.path} path={r.path} element={r.element} />)}
                        {posRoutes.map(r => <Route key={r.path} path={r.path} element={r.element} />)}
                        {superadminRoutes.map(r => <Route key={r.path} path={r.path} element={r.element} />)}
                      </Routes>
                      <ModalOverlay />
                      <ForcePasswordChangeModal />
                    </>
                  </ThemeProvider>
                </NotificationProvider>
              </AppErrorBoundary>
            }
          />
        </Routes>
        {/* Global ToastContainer with close button enabled - single source for all toasts */}
        {/* Position: top-center (POS best practice) - doesn't block action buttons or forms */}
        <ToastContainer
          position="top-center"
          autoClose={3000}
          closeButton={true}
          pauseOnHover={true}
          draggable={true}
          limit={3}
          newestOnTop
          hideProgressBar={false}
        />
        <NetworkStatusNotification />
        <SessionExpiredModal />
      </Router>
    </Provider>
  );
}

export default App;
